import React, { useEffect, useState } from "react";
import NoteIcon from "../components/NoteIcon";
import { useHeaderContext } from "../context/HeaderContext";
import { supabase } from "../services/supabase";
import { summarizeContent } from "../services/groqSummarizer";
import { GroqDrawerTrigger } from "../components/GroqDrawerTrigger";
import WPAVendorBanner from "../components/WPAVendorBanner";
import VendorRagSearch from "../components/VendorRagSearch";

// --- Add this VendorCarousel component ---
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

export function VendorCarousel({ images = [] }) {
  if (!images.length) return null;

  // Hide arrows/dots if only one image
  const showArrows = images.length > 1;
  const showDots = images.length > 1;

  const settings = {
    dots: showDots,
    arrows: showArrows,
    infinite: true,
    speed: 400,
    slidesToShow: 1,
    slidesToScroll: 1,
    swipe: true,
    draggable: true,
    lazyLoad: "ondemand",
    nextArrow: (
      <button
        type="button"
        className="slick-arrow slick-next !right-2 !z-20 bg-gray-800/80 text-white rounded-full p-1 text-lg sm:text-2xl"
        aria-label="Next"
      >
        &rarr;
      </button>
    ),
    prevArrow: (
      <button
        type="button"
        className="slick-arrow slick-prev !left-2 !z-20 bg-gray-800/80 text-white rounded-full p-1 text-lg sm:text-2xl"
        aria-label="Previous"
      >
        &larr;
      </button>
    ),
    appendDots: (dots) => (
      <div>
        <ul className="!mb-0 flex justify-center">{dots}</ul>
      </div>
    ),
    customPaging: (i) => (
      <div className="w-2 h-2 bg-gray-400 rounded-full mx-1"></div>
    ),
    responsive: [
      {
        breakpoint: 640,
        settings: {
          arrows: showArrows,
          dots: showDots,
        },
      },
    ],
  };

  return (
    <div className="mb-4">
      <Slider {...settings}>
        {images.map((url, idx) => (
          <div key={idx} className="flex justify-center items-center">
            <img
              src={url}
              alt={`Vendor image ${idx + 1}`}
              loading="lazy"
              className="w-full max-h-64 object-cover rounded-xl shadow border border-gray-200 dark:border-gray-700 bg-white"
              style={{ maxWidth: 320 }}
            />
          </div>
        ))}
      </Slider>
    </div>
  );
}

// --- Update VendorModal to use VendorCarousel ---
function VendorModal({ vendor, onClose }) {
  if (!vendor) return null;
  let imageUrls = [];
  if (vendor.image_urls && typeof vendor.image_urls === "string") {
    imageUrls = vendor.image_urls
      .split(",")
      .map((url) => url.trim())
      .filter(Boolean);
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 max-w-md w-full relative max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 z-10 flex justify-end bg-transparent">
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-900 dark:hover:text-white text-2xl font-bold p-2 bg-white/80 dark:bg-gray-800/80 rounded-full shadow"
            aria-label="Close"
            style={{ marginRight: '-0.5rem', marginTop: '-0.5rem' }}
          >
            ×
          </button>
        </div>
        {/* Carousel for images */}
        {imageUrls.length > 0 && <VendorCarousel images={imageUrls} />}
        <h2 className="mb-2 text-gray-700 dark:text-gray-300"
          style={{
            fontFamily: 'Times New Roman, Times, serif',
            fontSize: '1.25em',
            fontWeight: 400,
            fontStyle: 'normal',
            letterSpacing: '0.5px'
          }}
        >
          {vendor.vendor_name || "Unnamed Vendor"}
        </h2>
        <div className="text-sm text-gray-700 dark:text-gray-300 mb-1">
          <span className="font-medium">State:</span> {vendor.state || "N/A"}
        </div>
        <div className="text-sm text-gray-700 dark:text-gray-300 mb-1">
          <span className="font-medium">Category:</span> {vendor.category || "N/A"}
        </div>
        <div className="text-gray-700 dark:text-gray-300 mt-4 whitespace-pre-line">
          {vendor.content || "No description."}
        </div>
      </div>
    </div>
  );
}

const VendorsPage = () => {

  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const pageSize = 4;
  const [isLastPage, setIsLastPage] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Search Supabase state
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  // Embedding state
  const [embedding, setEmbedding] = useState(null);
  const [embeddingResult, setEmbeddingResult] = useState("");
  const [embeddingLoading, setEmbeddingLoading] = useState(false);

  // Modal state
  const [modalVendor, setModalVendor] = useState(null);

  // Track selected vendor for 'Use in Field Notes' (only one allowed)
  const [selectedVendorId, setSelectedVendorId] = useState(null);
  const { setGroqContext, setLocation } = useHeaderContext();

  // Fetch total count once on mount
  // Supabase full-text search for vendor_chunks
  const runSearchQuery = async () => {
    if (!searchTerm.trim()) return;
    setSearchError(null);
    setSearchLoading(true);
    try {
      const { data, error } = await supabase
        .from("vendor_chunks")
        .select("vendor_name, content")
        .textSearch("content", searchTerm, { type: "websearch" })
        .limit(10);
      if (error) throw error;
      setSearchResults(data);
    } catch (err) {
      setSearchError(err.message);
    } finally {
      setSearchLoading(false);
    }
  };

  // 1. Generate embedding for the query using OpenAI API (or your embedding API)
  /*
  const handleGenerateEmbedding = async () => {
    if (!searchTerm.trim()) return;
    setEmbeddingLoading(true);
    setEmbeddingResult("");
    try {
      // Example: Replace this with your actual embedding API endpoint and key
      const response = await fetch("https://api.openai.com/v1/embeddings", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer YOUR_OPENAI_API_KEY` // <-- Replace with your key or use a proxy
        },
        body: JSON.stringify({
          model: "text-embedding-3-small", // or your model
          input: searchTerm
        })
      });
      const data = await response.json();
      if (data && data.data && data.data[0] && data.data[0].embedding) {
        setEmbedding(data.data[0].embedding);
        setEmbeddingResult("Embedding generated (length: " + data.data[0].embedding.length + ")");
      } else {
        setEmbedding(null);
        setEmbeddingResult("Failed to generate embedding.");
      }
    } catch (err) {
      setEmbedding(null);
      setEmbeddingResult("Error generating embedding: " + err.message);
    } finally {
      setEmbeddingLoading(false);
    }
  };
  */

  // 2. Store embedding in Supabase for the current query
  const handleStoreEmbedding = async () => {
    if (!embedding || !searchTerm.trim()) {
      setEmbeddingResult("No embedding to store. Generate one first.");
      return;
    }
    setEmbeddingLoading(true);
    setEmbeddingResult("");
    try {
      // Store as a new row in vendor_chunks (customize as needed)
      const { error } = await supabase
        .from("vendor_chunks")
        .insert([
          {
            vendor_name: "Manual Query",
            content: searchTerm,
            embedding: embedding
          }
        ]);
      if (error) throw error;
      setEmbeddingResult("Embedding stored in Supabase.");
    } catch (err) {
      setEmbeddingResult("Error storing embedding: " + err.message);
    } finally {
      setEmbeddingLoading(false);
    }
  };

  // 3. Fetch most similar embedding from Supabase for the current query
  const handleFetchEmbedding = async () => {
    if (!embedding || !searchTerm.trim()) {
      setEmbeddingResult("No embedding to search with. Generate one first.");
      return;
    }
    setEmbeddingLoading(true);
    setEmbeddingResult("");
    try {
      // Use the pgvector <-> operator for similarity search (cosine distance)
      // This query assumes you have the vector extension enabled and the embedding column is vector(384)
      const { data, error } = await supabase.rpc("match_vendor_chunks", {
        query_embedding: embedding,
        match_count: 5
      });
      if (error) throw error;
      setEmbeddingResult(
        data && data.length
          ? `Top matches:\n` + data.map((row, i) => `${i + 1}. ${row.vendor_name} (score: ${row.score})`).join("\n")
          : "No matches found."
      );
    } catch (err) {
      setEmbeddingResult("Error fetching similar embeddings: " + err.message);
    } finally {
      setEmbeddingLoading(false);
    }
  };
  useEffect(() => {
    const fetchTotalCount = async () => {
      const { count, error } = await supabase
        .from("vendor_chunks")
        .select("ID", { count: "exact", head: true });
      if (!error && typeof count === "number") {
        setTotalCount(count);
      }
    };
    fetchTotalCount();
  }, []);

  useEffect(() => {
    const fetchVendors = async () => {
      setLoading(true);
      setError("");
      setIsLastPage(false);
      const { data, error } = await supabase
        .from("vendor_chunks")
        .select("ID, vendor_name, state, category, content, image_urls")
        .order("ID", { ascending: false })
        .range(page * pageSize, page * pageSize + pageSize - 1);
      if (error) {
        setError("Failed to load vendors.");
        setVendors([]);
      } else {
        setVendors(data || []);
        if (!data || data.length < pageSize) setIsLastPage(true);
      }
      setLoading(false);
    };
    fetchVendors();
  }, [page]);

  return (
    <div className="px-2 sm:px-4 md:px-8 py-4 sm:py-8">
      {/* GroqDrawer Trigger Button and Drawer */}
      <GroqDrawerTrigger />
      {/* Modal for vendor details */}
      <VendorModal vendor={modalVendor} onClose={() => setModalVendor(null)} />
      {/* Removed top image and text, now handled inside WPAVendorBanner */}
      <WPAVendorBanner />
      {loading ? (
        <div className="text-gray-600 dark:text-gray-300">Loading vendors...</div>
      ) : error ? (
        <div className="text-red-600 dark:text-red-400">{error}</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {vendors.map((vendor) => {
              let imageUrl = null;
              if (vendor.image_urls && typeof vendor.image_urls === 'string') {
                imageUrl = vendor.image_urls.split(',')[0].trim();
              }
              const checked = selectedVendorId === vendor.ID;
              const handleRadio = async () => {
                setSelectedVendorId(vendor.ID);
                setLocation({ name: vendor.vendor_name || "", lat: undefined, lon: undefined });
                try {
                  const summary = await summarizeContent(vendor.content || "");
                  setGroqContext(summary);
                } catch (err) {
                  setGroqContext("(Summary unavailable)");
                }
              };
              return (
                <div
                  key={vendor.ID}
                  className="bg-gradient-to-r from-green-400/80 to-blue-400/80 dark:from-green-700/80 dark:to-blue-700/80 border border-green-200 dark:border-green-700 rounded-xl p-4 sm:p-6 shadow-lg flex flex-col justify-between"
                >
                  <div>
                    {imageUrl && (
                      <div className="flex justify-center mb-3">
                        <img
                          src={imageUrl}
                          alt={vendor.vendor_name || 'Vendor'}
                          className="w-20 h-20 sm:w-32 sm:h-32 object-cover rounded-xl shadow border border-gray-200 dark:border-gray-700 bg-white"
                        />
                      </div>
                    )}
                    <h2 className="mb-2 text-gray-700 dark:text-gray-300"
                      style={{
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '1.25em',
                        fontWeight: 400,
                        fontStyle: 'normal',
                        letterSpacing: '0.5px'
                      }}
                    >
                      {vendor.vendor_name || "Unnamed Vendor"}
                    </h2>
                    <div className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                      <span className="font-medium">State:</span> {vendor.state || "N/A"}
                    </div>
                    <div className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                      <span className="font-medium">Category:</span> {vendor.category || "N/A"}
                    </div>
                    <p className="text-gray-700 dark:text-gray-300 mb-2 line-clamp-3">{vendor.content || "No description."}</p>
                  </div>
                  {/* Radio button above the buttons */}
                  <div className="flex items-center mb-2 mt-2">
                    <input
                      id={`field-notes-radio-${vendor.ID}`}
                      type="radio"
                      name="field-notes-radio"
                      checked={checked}
                      onChange={handleRadio}
                      className="form-radio h-4 w-4 text-blue-600 transition duration-150 ease-in-out mr-2"
                    />
                    <label htmlFor={`field-notes-radio-${vendor.ID}`} className="flex items-center text-sm text-gray-800 dark:text-gray-100 select-none gap-1">
                      <span className="w-4 h-4 flex items-center justify-center"><NoteIcon /></span>
                      Use in Field Notes
                    </label>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <button
                      className="flex-1 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium py-2 px-3 rounded-lg transition-colors"
                      onClick={() => setModalVendor(vendor)}
                    >
                      View
                    </button>
                    <a
                      className="flex-1 bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-sm font-medium py-2 px-3 rounded-lg transition-colors text-center cursor-pointer flex items-center justify-center gap-1"
                      href={`https://www.google.com/maps/search/?api=1&query=${(vendor.vendor_name || '').trim().replace(/\s+/g, '+')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-blue-600 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c1.104 0 2-.896 2-2s-.896-2-2-2-2 .896-2 2 .896 2 2 2zm0 10c-4.418 0-8-5.373-8-10a8 8 0 1116 0c0 4.627-3.582 10-8 10z" />
                      </svg>
                      Locate
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Pagination Arrows and Count */}
          <div className="flex flex-wrap justify-center items-center mt-8 gap-2 sm:gap-4">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className={`text-3xl px-4 py-2 rounded-full transition-colors ${page === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-100'}`}
              aria-label="Previous"
            >
              &#8592;
            </button>
            <button
              onClick={() => setPage((p) => (isLastPage ? p : p + 1))}
              disabled={isLastPage}
              className={`text-3xl px-4 py-2 rounded-full transition-colors ${isLastPage ? 'text-gray-300 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-100'}`}
              aria-label="Next"
            >
              &#8594;
            </button>
            <span className="ml-4 text-lg text-gray-700 dark:text-gray-200 min-w-[120px] text-right">
              {totalCount > 0
                ? `${page * pageSize + 1}-${Math.min((page + 1) * pageSize, totalCount)} of ${totalCount}`
                : ''}
            </span>
          </div>
        </>
      )}

      {/* Supabase Search Section */}
      <div className="max-w-2xl mx-auto mt-8 sm:mt-12">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-8 mb-8">
          <h2 className="mb-2 text-gray-700 dark:text-gray-300"
            style={{
              fontFamily: 'Times New Roman, Times, serif',
              fontSize: '1.25em',
              fontWeight: 400,
              fontStyle: 'normal',
              letterSpacing: '0.5px'
            }}
          >
            Search Vendor Keywords
          </h2>
          <div className="flex flex-col sm:flex-row gap-2 mb-4 sm:mb-6">
            <input
              type="text"
              className="border p-3 rounded flex-1 text-gray-900 bg-white dark:text-gray-100 dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Enter search text..."
            />
            <button
              onClick={runSearchQuery}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-medium transition-all duration-200"
            >
              Search Supabase
            </button>
          </div>
          {searchLoading && <p className="text-blue-600">Loading Supabase results...</p>}
          {searchError && <p className="text-red-600 mt-4">{searchError}</p>}
          {embeddingResult && (
            <div className="mt-2 p-2 border rounded bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-100 whitespace-pre-line">
              {embeddingResult}
            </div>
          )}
          {searchResults.length > 0 && (
            <div className="mt-4 p-4 border rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
              <h3 className="font-bold mb-2">Supabase Results</h3>
              <ul className="list-disc pl-5 space-y-4">
                {searchResults.map((row, idx) => (
                  <li key={idx}>
                    <p className="font-semibold text-blue-700 dark:text-blue-400">{row.vendor_name}</p>
                    <p className="text-sm">{row.content}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* VendorRagSearch Component */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-8">
          <h2 className="mb-2 text-gray-700 dark:text-gray-300"
            style={{
              fontFamily: 'Times New Roman, Times, serif',
              fontSize: '1.25em',
              fontWeight: 400,
              fontStyle: 'normal',
              letterSpacing: '0.5px'
            }}
          >
            Smart Vendor Suggestions
          </h2>
          <VendorRagSearch 
            onVendorSelect={async (vendor) => {
              setSelectedVendorId(vendor.ID);
              setLocation({ name: vendor.vendor_name || "", lat: undefined, lon: undefined });
              try {
                const summary = await summarizeContent(vendor.content || "");
                setGroqContext(summary);
              } catch (err) {
                setGroqContext("(Summary unavailable)");
              }
            }}
            onVendorView={(vendor) => setModalVendor(vendor)}
            selectedVendorId={selectedVendorId}
          />
        </div>
      </div>
    </div>
  );
};

export default VendorsPage;
